import { useState } from 'react';
import {
    Terminal,
    Zap,
    Brain,
    Calculator,
    Globe,
    Lock,
    Shield,
    Atom,
    Clock,
    TrendingUp,
    AlertTriangle,
    BookOpen,
    Cpu,
    Target,
    Infinity,
    Eye,
    EyeOff,
    Copy,
    Play,
    Pause
} from 'lucide-react';

export default function BtcInfo() {
    const [activeSection, setActiveSection] = useState('intro');
    const [showFullNumber, setShowFullNumber] = useState(false);
    const [animatingCounter, setAnimatingCounter] = useState(false);
    const [currentCount, setCurrentCount] = useState(0);

    // Números importantes - COMPLETOS PARA MÁXIMO SHOCK CON COMAS
    const totalWallets = "115,792,089,237,316,195,423,570,985,008,687,907,853,269,984,665,640,564,039,457,584,007,913,129,639,936";
    const atomsInUniverse = "100,000,000,000,000,000,000,000,000,000,000,000,000,000,000,000,000,000,000,000,000,000,000,000,000,000,000"; // 10^80
    const secondsInYear = 31536000;
    const universeBirthYears = 13800000000;
    const universeAgeSeconds = secondsInYear * universeBirthYears; // 435,283,200,000,000,000
    const triesPerSecond = "1,000,000,000,000"; // 1 trillón por segundo
    const yearsToTry50Percent = "2,895,845,208,713,398,632,274,468,162,706,568,951,176,284,665,415,202,893,673,473,623,490,838,175,239,424"; // Años necesarios para 50% chance
    const humanPopulation = "8,000,000,000"; // 8 mil millones
    const walletsPerHuman = "1,000,000,000,000"; // 1 trillón por persona
    const totalHumanWallets = "8,000,000,000,000,000,000,000"; // Si cada humano tuviera 1 trillón

    // Simulador de búsqueda de wallet de Satoshi
    const startSatoshiSearch = () => {
        setAnimatingCounter(true);
        let count = 0;
        const interval = setInterval(() => {
            count += Math.floor(Math.random() * 1000000) + 100000;
            setCurrentCount(count);

            // Parar después de unos segundos para mostrar lo inútil que es
            if (count > 10000000) {
                clearInterval(interval);
                setAnimatingCounter(false);
                setTimeout(() => setCurrentCount(0), 3000);
            }
        }, 100);
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            alert('¡Número copiado! (Buena suerte contándolo 😄)');
        } catch (err) {
            console.error('Error copying:', err);
        }
    };

    const sections = [
        { id: 'intro', title: 'INTRO', icon: BookOpen },
        { id: 'math', title: 'MATH', icon: Calculator },
        { id: 'comparison', title: 'SCALE', icon: Globe },
        { id: 'satoshi', title: 'SATOSHI', icon: Target },
        { id: 'security', title: 'SECURITY', icon: Shield }
    ];

    return (
        <div className="min-h-screen bg-black text-green-400 font-mono overflow-x-hidden">
            {/* Matrix Background */}
            <div className="fixed inset-0 opacity-5 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-b from-green-900/20 via-black to-green-900/20"></div>
            </div>

            <div className="relative z-10 p-3">
                {/* Header */}
                <div className="text-center mb-4">
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <Terminal className="w-6 h-6 text-green-400 animate-pulse" />
                        <h1 className="text-xl font-bold text-green-400 tracking-wider">
                            BTC_MATHEMATICS.EXE
                        </h1>
                        <Brain className="w-4 h-4 text-yellow-400" />
                    </div>
                    <div className="text-xs text-green-600">
                        [EDUCATIONAL_MODE] STATUS: LEARNING_ACTIVE
                    </div>
                </div>

                {/* Navigation Tabs */}
                <div className="mb-4">
                    <div className="grid grid-cols-5 gap-1 bg-gray-900/50 p-1 rounded-lg border border-green-500/30">
                        {sections.map((section) => {
                            const IconComponent = section.icon;
                            return (
                                <button
                                    key={section.id}
                                    onClick={() => setActiveSection(section.id)}
                                    className={`p-2 rounded text-xs font-mono transition-all ${activeSection === section.id
                                        ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                                        : 'text-green-600 hover:text-green-400'
                                        }`}
                                >
                                    <IconComponent className="w-4 h-4 mx-auto mb-1" />
                                    {section.title}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Content Sections */}
                <div className="bg-gray-900/50 border border-green-500/30 rounded-lg p-4 mb-4">

                    {/* INTRO Section */}
                    {activeSection === 'intro' && (
                        <div className="space-y-4">
                            <div className="text-center">
                                <BookOpen className="w-8 h-8 mx-auto text-green-400 mb-2" />
                                <h2 className="text-sm font-bold text-green-400 mb-2">¿CUÁNTAS WALLETS EXISTEN?</h2>
                                <p className="text-xs text-green-600 leading-relaxed">
                                    Bitcoin utiliza criptografía de curva elíptica (secp256k1) con claves privadas de 256 bits.
                                    Esto significa que hay un número ASTRONÓMICO de wallets posibles.
                                </p>
                            </div>

                            <div className="bg-black/50 border border-green-500/30 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <Calculator className="w-4 h-4 text-yellow-400" />
                                    <span className="text-xs font-bold text-yellow-400">FÓRMULA BÁSICA</span>
                                </div>
                                <div className="text-center space-y-2">
                                    <div className="text-green-400 font-mono text-sm">2^256 = ?</div>
                                    <div className="text-xs text-green-600">
                                        Cada bit puede ser 0 ó 1<br />
                                        256 bits = 2 × 2 × 2... (256 veces)
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2 text-xs">
                                <div className="bg-blue-500/20 border border-blue-500/30 rounded p-2 text-center">
                                    <div className="text-blue-400 font-bold">BITS</div>
                                    <div className="text-blue-600">256</div>
                                </div>
                                <div className="bg-purple-500/20 border border-purple-500/30 rounded p-2 text-center">
                                    <div className="text-purple-400 font-bold">CURVA</div>
                                    <div className="text-purple-600">secp256k1</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* MATH Section */}
                    {activeSection === 'math' && (
                        <div className="space-y-4">
                            <div className="text-center">
                                <Calculator className="w-8 h-8 mx-auto text-green-400 mb-2" />
                                <h2 className="text-sm font-bold text-green-400 mb-2">EL NÚMERO COMPLETO</h2>
                                <p className="text-xs text-green-600">
                                    Aquí está el número EXACTO de wallets Bitcoin posibles:
                                </p>
                            </div>

                            {/* El número gigante */}
                            <div className="bg-black/50 border border-yellow-500/30 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs text-yellow-400 font-bold">TOTAL_WALLETS_POSIBLES:</span>
                                    <div className="flex gap-1">
                                        <button
                                            onClick={() => copyToClipboard(totalWallets)}
                                            className="text-yellow-400 hover:text-yellow-300 p-1"
                                        >
                                            <Copy className="w-3 h-3" />
                                        </button>
                                    </div>
                                </div>

                                <div className="text-yellow-400 font-mono text-xs break-all leading-relaxed border border-yellow-500/50 p-2 rounded bg-black/70">
                                    {totalWallets}
                                </div>
                                <div className="text-center mt-2">
                                    <div className="text-xs text-yellow-600">
                                        ☝️ ESE es el número COMPLETO de 78 dígitos
                                    </div>
                                </div>
                            </div>

                            <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <Brain className="w-4 h-4 text-green-400" />
                                    <span className="text-xs font-bold text-green-400">PARA ENTENDERLO</span>
                                </div>
                                <div className="text-xs text-green-600 space-y-1">
                                    <div>• Tiene 78 dígitos</div>
                                    <div>• Es aproximadamente 1.16 × 10^77</div>
                                    <div>• Cada clave privada = 1 wallet única</div>
                                    <div>• Relación 1:1 matemáticamente garantizada</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* COMPARISON Section */}
                    {activeSection === 'comparison' && (
                        <div className="space-y-4">
                            <div className="text-center">
                                <Globe className="w-8 h-8 mx-auto text-green-400 mb-2" />
                                <h2 className="text-sm font-bold text-green-400 mb-2">ESCALA CÓSMICA</h2>
                                <p className="text-xs text-green-600">
                                    Para entender qué tan grande es este número, comparémoslo con el universo:
                                </p>
                            </div>

                            <div className="space-y-3">
                                <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Atom className="w-4 h-4 text-blue-400" />
                                        <span className="text-xs font-bold text-blue-400">ÁTOMOS EN EL UNIVERSO OBSERVABLE</span>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-blue-400 font-mono text-xs break-all border border-blue-500/50 p-2 rounded bg-black/70">
                                            {atomsInUniverse}
                                        </div>
                                        <div className="text-xs text-blue-600 mt-1">
                                            ☝️ Estimación científica: 10^80 átomos
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Lock className="w-4 h-4 text-yellow-400" />
                                        <span className="text-xs font-bold text-yellow-400">WALLETS BITCOIN POSIBLES</span>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-yellow-400 font-mono text-xs break-all border border-yellow-500/50 p-2 rounded bg-black/70">
                                            {totalWallets}
                                        </div>
                                        <div className="text-xs text-yellow-600 mt-1">
                                            ☝️ 2^256 claves privadas exactas
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                        <AlertTriangle className="w-4 h-4 text-red-400" />
                                        <span className="text-xs font-bold text-red-400">🤯 SHOCK MENTAL</span>
                                    </div>
                                    <div className="text-xs text-red-400 text-center space-y-1">
                                        <div>Bitcoin tiene MÁS wallets que átomos en el universo!</div>
                                        <div>Específicamente: 1,157 veces más wallets que átomos</div>
                                        <div>Si cada átomo fuera un universo completo...</div>
                                        <div>¡TODAVÍA no tendríamos suficientes átomos!</div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-700/50 border border-gray-500/30 rounded-lg p-3">
                                <div className="text-xs text-green-600 space-y-2">
                                    <div className="font-bold text-green-400 mb-2">🤯 NÚMEROS SHOCK COMPLETOS:</div>

                                    <div className="space-y-1">
                                        <div className="text-purple-400 font-bold">Si TODA la humanidad tuviera wallets:</div>
                                        <div>• Humanos en la Tierra: {humanPopulation}</div>
                                        <div>• Si cada uno tuviera 1 TRILLÓN de wallets: {walletsPerHuman}</div>
                                        <div>• Total wallets humanas: <span className="text-purple-400 font-mono">{totalHumanWallets}</span></div>
                                        <div className="text-red-400">• ¡Solo usaríamos 0.0000000000000000000000000000000000000000000000000000069% del espacio!</div>
                                    </div>

                                    <div className="mt-3 p-2 border border-red-500/30 rounded bg-red-500/10">
                                        <div className="text-red-400 font-bold text-center">
                                            CONCLUSIÓN: Es más probable que te impacte un meteorito<br />
                                            mientras ganas la lotería 50 veces seguidas<br />
                                            que encontrar una wallet ajena por accidente.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* SATOSHI Section */}
                    {activeSection === 'satoshi' && (
                        <div className="space-y-4">
                            <div className="text-center">
                                <Target className="w-8 h-8 mx-auto text-green-400 mb-2" />
                                <h2 className="text-sm font-bold text-green-400 mb-2">🎯 CHALLENGE: ¡ENCUENTRA UNA WALLET CON BALANCE!</h2>
                                <p className="text-xs text-green-600">
                                    Te dejamos un generador de wallets Bitcoin.<br />
                                    ¡Intenta encontrar UNA SOLA wallet que tenga balance!
                                </p>
                                <p className="text-xs text-yellow-400 mt-1">
                                    Spoiler: Vas a perder tu tiempo... matemáticamente 😅
                                </p>
                            </div>

                            <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <AlertTriangle className="w-4 h-4 text-red-400" />
                                    <span className="text-xs font-bold text-red-400">SIMULADOR DE BÚSQUEDA REALISTA</span>
                                </div>
                                <div className="text-center space-y-2">
                                    <div className="text-red-400 font-mono text-lg">
                                        INTENTOS: {currentCount.toLocaleString()}
                                    </div>
                                    <div className="text-xs text-red-600 space-y-1">
                                        <div>Wallets generadas: {currentCount.toLocaleString()}</div>
                                        <div>Wallets con balance encontradas: 0</div>
                                        <div>Probabilidad acumulada: 0.00000000000000000000000000000000000000000000000000000000000000000000000001%</div>
                                    </div>
                                    <button
                                        onClick={startSatoshiSearch}
                                        disabled={animatingCounter}
                                        className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-2 rounded font-mono text-xs hover:bg-red-500/30 transition-colors flex items-center gap-2 mx-auto"
                                    >
                                        {animatingCounter ? (
                                            <>
                                                <Pause className="w-3 h-3" />
                                                BUSCANDO_BALANCE...
                                            </>
                                        ) : (
                                            <>
                                                <Play className="w-3 h-3" />
                                                BUSCAR_WALLET_CON_DINERO
                                            </>
                                        )}
                                    </button>
                                    <div className="text-xs text-red-600">
                                        (Este simulador se rendirá... como todos los hackers 😂)
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Calculator className="w-4 h-4 text-yellow-400" />
                                        <span className="text-xs font-bold text-yellow-400">PROBABILIDAD REAL (NÚMEROS COMPLETOS)</span>
                                    </div>
                                    <div className="text-xs text-yellow-600 space-y-2">
                                        <div>• <span className="text-yellow-400">Chance de acertar en 1 intento:</span> 1 en {totalWallets}</div>
                                        <div>• <span className="text-yellow-400">Para encontrar la wallet de Satoshi específicamente:</span><br />
                                            1 en {totalWallets}</div>
                                        <div>• <span className="text-yellow-400">Para contexto:</span> Es como si tiraras un dado que tuviera {totalWallets} caras<br />
                                            y necesitaras sacar exactamente el número 1</div>
                                    </div>
                                </div>

                                <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Clock className="w-4 h-4 text-blue-400" />
                                        <span className="text-xs font-bold text-blue-400">TIEMPO NECESARIO (NÚMEROS REALES)</span>
                                    </div>
                                    <div className="text-xs text-blue-600 space-y-2">
                                        <div>• <span className="text-blue-400">Si probaras 1 trillón de wallets por segundo:</span><br />
                                            <span className="font-mono text-blue-300">{triesPerSecond}</span> intentos/segundo</div>
                                        <div>• <span className="text-blue-400">Necesitarías estos años para tener 50% de chance:</span><br />
                                            <span className="font-mono text-blue-300 break-all">{yearsToTry50Percent}</span> años</div>
                                        <div>• <span className="text-blue-400">El universo tiene:</span> 13,800,000,000 años</div>
                                        <div className="text-red-400 font-bold">• ¡Necesitarías que el universo renaciera más veces de las que hay átomos en él!</div>
                                    </div>
                                </div>

                                <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Infinity className="w-4 h-4 text-purple-400" />
                                        <span className="text-xs font-bold text-purple-400">REALIDAD BRUTAL</span>
                                    </div>
                                    <div className="text-xs text-purple-600 space-y-1">
                                        <div>🌌 Si cada átomo del universo fuera una supercomputadora</div>
                                        <div>⚡ Y cada una probara 1 trillón de wallets por segundo</div>
                                        <div>🕰️ Durante toda la vida del universo hasta ahora</div>
                                        <div className="text-red-400 font-bold text-center">¡AÚN NO HABRÍAMOS ENCONTRADO NI UNA WALLET DE SATOSHI!</div>
                                    </div>
                                </div>

                                <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Shield className="w-4 h-4 text-green-400" />
                                        <span className="text-xs font-bold text-green-400">VEREDICTO CIENTÍFICO</span>
                                    </div>
                                    <div className="text-xs text-green-400 text-center font-bold space-y-1">
                                        <div>Es MATEMÁTICAMENTE IMPOSIBLE</div>
                                        <div>hackear Bitcoin por fuerza bruta.</div>
                                        <div>Tienes más probabilidad de:</div>
                                        <div className="text-yellow-400">• Ganar la lotería 10 veces seguidas</div>
                                        <div className="text-yellow-400">• Ser impactado por un rayo 100 veces</div>
                                        <div className="text-yellow-400">• Encontrar vida extraterrestre mañana</div>
                                        <div className="text-red-400 mt-2">¡QUE DE HACKEAR UNA WALLET BITCOIN! 💀</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* SECURITY Section */}
                    {activeSection === 'security' && (
                        <div className="space-y-4">
                            <div className="text-center">
                                <Shield className="w-8 h-8 mx-auto text-green-400 mb-2" />
                                <h2 className="text-sm font-bold text-green-400 mb-2">SEGURIDAD CUÁNTICA</h2>
                                <p className="text-xs text-green-600">
                                    ¿Por qué Bitcoin es tan seguro y qué pasa con las computadoras cuánticas?
                                </p>
                            </div>

                            <div className="space-y-3">
                                <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Lock className="w-4 h-4 text-green-400" />
                                        <span className="text-xs font-bold text-green-400">FORTALEZA ACTUAL</span>
                                    </div>
                                    <div className="text-xs text-green-600 space-y-1">
                                        <div>✅ Cada wallet es única (colisión prácticamente imposible)</div>
                                        <div>✅ Criptografía probada durante 15+ años</div>
                                        <div>✅ Respaldado por matemáticas, no por promesas</div>
                                        <div>✅ Más seguro que la mayoría de bancos</div>
                                    </div>
                                </div>

                                <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Cpu className="w-4 h-4 text-purple-400" />
                                        <span className="text-xs font-bold text-purple-400">COMPUTACIÓN CUÁNTICA</span>
                                    </div>
                                    <div className="text-xs text-purple-600 space-y-1">
                                        <div>🔮 Algoritmo de Shor podría romper ECC</div>
                                        <div>⏰ Pero necesitaríamos ~4000 qubits estables</div>
                                        <div>📈 Actualmente tenemos ~1000 qubits (inestables)</div>
                                        <div>🛡️ Bitcoin puede migrar a criptografía post-cuántica</div>
                                    </div>
                                </div>

                                <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                        <TrendingUp className="w-4 h-4 text-yellow-400" />
                                        <span className="text-xs font-bold text-yellow-400">TIMELINE REALISTA</span>
                                    </div>
                                    <div className="text-xs text-yellow-600 space-y-1">
                                        <div>📅 2024: Bitcoin sigue siendo seguro</div>
                                        <div>📅 2030-2040: Primeras amenazas cuánticas posibles</div>
                                        <div>📅 2025-2035: Bitcoin probablemente actualizará su criptografía</div>
                                        <div>📅 Conclusión: Tenemos tiempo de sobra para adaptarnos</div>
                                    </div>
                                </div>

                                <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Brain className="w-4 h-4 text-blue-400" />
                                        <span className="text-xs font-bold text-blue-400">EDUCACIÓN FINAL</span>
                                    </div>
                                    <div className="text-xs text-blue-600 space-y-1">
                                        <div>💡 Bitcoin no es "solo código", es matemática pura</div>
                                        <div>🔢 Los números no mienten: 2^256 es prácticamente infinito</div>
                                        <div>🌌 Tienes más chance de ser impactado por un meteorito</div>
                                        <div>🎯 ...que de encontrar la wallet de otra persona por accidente</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Fun Fact Footer */}
                <div className="text-center">
                    <div className="bg-gray-900/50 border border-green-500/30 rounded-lg p-3">
                        <div className="text-xs text-green-600 space-y-2">
                            <div className="flex items-center justify-center gap-2 mb-2">
                                <Infinity className="w-4 h-4" />
                                <span className="font-bold text-green-400">CHALLENGE IMPOSIBLE</span>
                                <Infinity className="w-4 h-4" />
                            </div>
                            <div className="font-mono text-green-400">
                                "Te dejamos un generador de wallets Bitcoin.<br />
                                ¡Intenta encontrar UNA SOLA con balance!"
                            </div>
                            <div className="text-red-400 font-bold">
                                Spoiler: Vas a perder tu tiempo... MATEMÁTICAMENTE 😂
                            </div>

                            <div className="bg-black/50 border border-green-500/30 rounded p-2 mt-3">
                                <div className="text-green-400 font-bold mb-1">WALLETS POSIBLES TOTALES:</div>
                                <div className="text-yellow-400 font-mono text-xs break-all">
                                    {totalWallets}
                                </div>
                                <div className="text-green-600 text-xs mt-1">
                                    ☝️ 78 dígitos de posibilidades infinitas
                                </div>
                            </div>

                            <div className="text-xs text-gray-400 mt-2">
                                Solo unas pocas millones de estas wallets tienen balance.<br />
                                Es como buscar una aguja en un pajar del tamaño del universo... ¡X1,000! 🎯
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}